import React from "react";
import { DB_COLLECTION_NAMES, Module, PossibleLocations } from "@constants";
import {
  CollectionConstraints,
  DEFAULT_COLLECTION_CONSTRAINTS,
  DEFAULT_PAGINATION_OPTS,
  PaginationOpts,
  useGetCollection,
} from "../useGetCollection";
import {
  CollectionQueryComparator,
  DEFAULT_COLLECTIONS_HOOK_RETURN,
  GetCollectionResp,
} from "../firestoreHookTypes";

/**
 * Custom hook to fetch modules from Firestore.
 * @param {CollectionQueryComparator[]} comparators Query options
 * @param {CollectionConstraints} constraints Query options
 * @param {PaginationOpts} paginationOpts Pagination options
 * @returns {GetCollectionResp<Module>}
 */
export function useGetModules(
  constraints: CollectionConstraints = DEFAULT_COLLECTION_CONSTRAINTS,
  paginationOpts: PaginationOpts = DEFAULT_PAGINATION_OPTS,
): GetCollectionResp<Module> {
  const modulesData = useGetCollection<Module>(
    DB_COLLECTION_NAMES.MODULES,
    constraints,
    paginationOpts,
  );
  if (modulesData.documents) return modulesData;
  return DEFAULT_COLLECTIONS_HOOK_RETURN;
}

/**
 * Wrapper for useGetModules that queries only for modules at a specified
 * location.
 * @param {string|undefined} location the location from which you want to get modules.
 * @returns {GetCollectionResp<Module>}
 */
export function useGetModulesByLocation(location = "") {
  const constraints = React.useMemo<CollectionConstraints>(() => {
    // Only pass comparators to useGetModules if the location is not empty
    if (location) {
      return {
        comparators: [
          {
            fieldOne: "location.location",
            fieldTwo: location,
            equalityComparator: "==",
          },
        ],
      };
    }
    return [];
  }, [location]);
  return useGetModules(constraints);
}

/**
 * Wrapper for useGetModules to get modules in transit to a specified origin
 * or destination
 * @param {PossibleLocations | undefined} origin
 * @param {PossibleLocations | undefined} destination
 */
export function useGetModulesInTransit(
  origin: PossibleLocations | undefined,
  destination: PossibleLocations | undefined,
) {
  const constraints = React.useMemo<CollectionConstraints>(() => {
    const tempComparators: CollectionQueryComparator[] = [
      {
        fieldOne: "location.inTransit",
        fieldTwo: true,
        equalityComparator: "==",
      },
    ];
    if (origin) {
      tempComparators.push({
        fieldOne: "location.origin",
        fieldTwo: origin,
        equalityComparator: "==",
      });
    }
    if (destination) {
      tempComparators.push({
        fieldOne: "location.destination",
        fieldTwo: destination,
        equalityComparator: "==",
      });
    }
    return { comparators: tempComparators };
  }, [origin, destination]);
  return useGetModules(constraints);
}

export function useGetModulesInTransitTo(
  destination: PossibleLocations | undefined,
) {
  return useGetModulesInTransit(undefined, destination);
}

export function useGetModulesInTransitFrom(
  origin: PossibleLocations | undefined,
) {
  return useGetModulesInTransit(origin, undefined);
}

export function useGetModulesByUser(userId: string) {
  const constraints = React.useMemo<CollectionConstraints>(() => {
    if (userId) {
      return {
        comparators: [
          {
            fieldOne: "contributors.contributorIds",
            fieldTwo: userId,
            equalityComparator: "array-contains",
          },
        ],
      };
    }
    return [];
  }, [userId]);
  return useGetModules(constraints);
}

export function useGetRecentModules(
  moduleLocation?: PossibleLocations,
  useBadModules?: boolean,
  count = 3,
) {
  const memoizedLocation = React.useMemo(
    () => moduleLocation,
    [moduleLocation],
  );
  const memoizedUseBadModules = React.useMemo(
    () => useBadModules,
    [useBadModules],
  );

  const constraints = React.useMemo<CollectionConstraints>(() => {
    if (!memoizedLocation) return [];

    const comparators: CollectionQueryComparator[] = [
      {
        fieldOne: "location.location",
        fieldTwo: memoizedLocation,
        equalityComparator: "==",
      },
    ];

    return {
      comparators,
      constraints: [
        {
          constraint: "orderBy",
          value: { key: "lastUpdated", direction: "desc" },
        },
      ],
    };
  }, [memoizedLocation]);

  /**
   * Note (AM): To properly limit the number of documents being displayed,
   * you have to add a pagination opt instead of a `limit` constraint because
   * of how `useGetModules` is implemented. So, this is "paginated", but only
   * will display the first page.
   */

  const paginationOpts = React.useMemo<PaginationOpts>(
    () => ({ size: count, page: 1 }),
    [count],
  );

  const { documents: fetchedModules, ...rest } = useGetModules(
    constraints,
    paginationOpts,
  );

  const filteredModules = React.useMemo(() => {
    if (!fetchedModules) return fetchedModules;

    if (memoizedUseBadModules) {
      return fetchedModules;
    } else {
      // This line will no longer cause a TypeScript error after the fix to models.ts
      return fetchedModules.filter(
        (module) => module.moduleStatus !== "do_not_use",
      );
    }
  }, [fetchedModules, memoizedUseBadModules]);

  return { documents: filteredModules, ...rest };
}
